import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api } from '../config';
import { useUser } from '../contexts/UserContext';
import { getDivisions } from '../utils/departments';
import './DelegateManagement.css';
import './ReviewerManagement.css';
import Spinner from './Spinner';
import '../styles/ResizableTable.css';
import { initResizableTables } from '../utils/resizableTables';
import { useTableSort } from '../utils/useTableSort';

interface Delegate {
  id: number;
  email: string;
  display_name: string | null;
  division: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const DelegateManagement: React.FC = () => {
  const { user } = useUser();
  const divisions = getDivisions();

  const [delegates, setDelegates] = useState<Delegate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newDelegateEmail, setNewDelegateEmail] = useState('');
  const [newDelegateDivision, setNewDelegateDivision] = useState('');
  const [isAddingDelegate, setIsAddingDelegate] = useState(false);
  const [updatingDivisionId, setUpdatingDivisionId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState('All');
  const itemsPerPage = 10;
  const [pageByCat, setPageByCat] = useState<Record<string, number>>({});

  useEffect(() => { loadDelegates(); }, []);

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => initResizableTables(), 50);
    }
  }, [isLoading]);

  const loadDelegates = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(api('/api/delegates/list'));
      setDelegates(response.data);
      setMessage(null);
    } catch {
      setMessage({ type: 'error', text: 'Failed to load delegates' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDelegate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDelegateEmail.trim()) { setMessage({ type: 'error', text: 'Please enter an email address' }); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newDelegateEmail)) { setMessage({ type: 'error', text: 'Please enter a valid email address' }); return; }
    if (!newDelegateDivision) { setMessage({ type: 'error', text: 'Please select a division' }); return; }
    if (!user?.email) { setMessage({ type: 'error', text: 'User not logged in' }); return; }

    try {
      setIsAddingDelegate(true);
      await axios.post(api('/api/delegates/add'), {}, {
        params: { email: newDelegateEmail.trim().toLowerCase(), requester_email: user.email, division: newDelegateDivision }
      });
      setNewDelegateEmail('');
      if (newDelegateDivision) setActiveTab(newDelegateDivision);
      setNewDelegateDivision('');
      setMessage({ type: 'success', text: 'Delegate added successfully' });
      setTimeout(() => setMessage(null), 3000);
      loadDelegates();
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message || 'Failed to add delegate';
      setMessage({ type: 'error', text: detail });
    } finally {
      setIsAddingDelegate(false);
    }
  };

  const handleRemoveDelegate = async (delegateId: number, delegateEmail: string) => {
    if (!window.confirm(`Remove ${delegateEmail} from the delegates list?`)) return;
    try {
      await axios.delete(api(`/api/delegates/remove/${delegateId}`));
      setMessage({ type: 'success', text: 'Delegate removed successfully' });
      setTimeout(() => setMessage(null), 3000);
      loadDelegates();
    } catch {
      setMessage({ type: 'error', text: 'Failed to remove delegate' });
    }
  };

  const handleUpdateDivision = async (delegate: Delegate, division: string) => {
    setUpdatingDivisionId(delegate.id);
    try {
      await axios.put(api(`/api/delegates/update/${delegate.id}`), {}, { params: { division } });
      setDelegates(prev => prev.map(d => d.id === delegate.id ? { ...d, division } : d));
    } catch {
      setMessage({ type: 'error', text: 'Failed to update division' });
    } finally {
      setUpdatingDivisionId(null);
    }
  };

  const assignedDivisions = Array.from(
    new Set(delegates.map(d => d.division).filter(Boolean) as string[])
  ).sort();
  const hasUnassigned = delegates.some(d => !d.division);
  const tabs = ['All', ...assignedDivisions, ...(hasUnassigned ? ['Unassigned'] : [])];
  const effectiveTab = tabs.includes(activeTab) ? activeTab : 'All';

  const tabDelegates = delegates.filter(d => {
    if (effectiveTab === 'All') return true;
    if (effectiveTab === 'Unassigned') return !d.division;
    return d.division === effectiveTab;
  });

  const currentPage = pageByCat[effectiveTab] || 1;
  const { sortedData: sortedDelegates, handleSort, sortIcon } = useTableSort<Delegate>(tabDelegates, 'email', 'asc', {
    created_at: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  });
  const totalPages = Math.ceil(sortedDelegates.length / itemsPerPage);
  const paginated = sortedDelegates.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const setPage = (p: number) => setPageByCat(prev => ({ ...prev, [effectiveTab]: p }));

  if (isLoading) {
    return (
      <div className="delegate-management">
        <Spinner message="Loading delegates..." />
      </div>
    );
  }

  return (
    <div className="delegate-management">
      {message && <div className={`message-alert ${message.type}`}>{message.text}</div>}

      <div className="add-delegate-section">
        <h3>Add New Delegate</h3>
        <form onSubmit={handleAddDelegate} className="add-delegate-form" style={{ flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ minWidth: '220px' }}>
            <label htmlFor="delegate-email">Email Address:</label>
            <input
              id="delegate-email"
              type="email"
              placeholder="delegate@example.com"
              value={newDelegateEmail}
              onChange={e => setNewDelegateEmail(e.target.value)}
              disabled={isAddingDelegate}
              className="email-input"
            />
          </div>
          <div className="form-group" style={{ minWidth: '150px' }}>
            <label htmlFor="delegate-division">Division:</label>
            <select
              id="delegate-division"
              value={newDelegateDivision}
              onChange={e => setNewDelegateDivision(e.target.value)}
              disabled={isAddingDelegate}
              className="email-input"
            >
              <option value="">--- Select ---</option>
              {divisions.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ minWidth: '120px', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={isAddingDelegate} className="btn-add-delegate" style={{ marginTop: '22px' }}>
              {isAddingDelegate ? 'Adding...' : 'Add Delegate'}
            </button>
          </div>
        </form>
      </div>

      <div className="reviewer-division-tabs">
        {tabs.map(tab => (
          <button
            key={tab}
            className={`reviewer-div-tab${effectiveTab === tab ? ' reviewer-div-tab--active' : ''}`}
            onClick={() => { setActiveTab(tab); setPage(1); }}
          >
            {tab}
            <span className="reviewer-div-tab-count">
              {tab === 'All' ? delegates.length
                : tab === 'Unassigned' ? delegates.filter(d => !d.division).length
                : delegates.filter(d => d.division === tab).length}
            </span>
          </button>
        ))}
      </div>

      <div className="delegates-list-section" style={{ marginTop: 0 }}>
        {tabDelegates.length === 0 ? (
          <div className="empty-state"><p>No delegates in this division.</p></div>
        ) : (
          <>
            <div className="delegates-table-container">
              <table id="manage-delegates-table" className="delegates-table">
                <thead>
                  <tr>
                    <th className="sortable-th" onClick={() => handleSort('email')}>Email {sortIcon('email')}</th>
                    <th className="sortable-th" onClick={() => handleSort('display_name')}>Display Name {sortIcon('display_name')}</th>
                    <th className="sortable-th" onClick={() => handleSort('division')}>Division {sortIcon('division')}</th>
                    <th className="sortable-th" onClick={() => handleSort('created_at')}>Added Date {sortIcon('created_at')}</th>
                    <th className="sortable-th" onClick={() => handleSort('is_active')}>Status {sortIcon('is_active')}</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(delegate => (
                    <tr key={delegate.id} className="delegate-row">
                      <td className="email-cell">{delegate.email}</td>
                      <td className="name-cell">
                        {delegate.display_name || <span style={{ color: '#999' }}>Not logged in yet</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <select
                            value={delegate.division || ''}
                            onChange={e => handleUpdateDivision(delegate, e.target.value)}
                            disabled={updatingDivisionId === delegate.id}
                            className="email-input"
                            style={{ padding: '4px 8px', height: '32px', fontSize: '13px' }}
                          >
                            <option value="">--- None ---</option>
                            {divisions.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                          {updatingDivisionId === delegate.id && (
                            <span style={{
                              display: 'inline-block',
                              width: '16px', height: '16px',
                              border: '2px solid #ddd',
                              borderTopColor: 'var(--abb-red)',
                              borderRadius: '50%',
                              animation: 'spin 0.7s linear infinite',
                              flexShrink: 0
                            }} />
                          )}
                        </div>
                      </td>
                      <td className="date-cell">
                        {new Date(delegate.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge ${delegate.is_active ? 'active' : 'inactive'}`}>
                          {delegate.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="action-cell">
                        <button className="btn-remove" onClick={() => handleRemoveDelegate(delegate.id, delegate.email)} title="Remove delegate">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="pagination-btn" onClick={() => setPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Prev</button>
                <span className="pagination-info">Page {currentPage} of {totalPages}</span>
                <button className="pagination-btn" onClick={() => setPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DelegateManagement;
